import { useState } from "react";
import { Package, Cpu, Download } from "lucide-react";
import PageBreadcrumb from "../../shared/PageBreadCrumb";
import ManufacturerTable from "./components/ManufacturerTable";
import MakeAndModelTable from "./components/MakeAndModelTable";
import ImportManufacturersPage from "./pages/ImportManufacturersPage";

const BRAND_COLORS = {
  primary: "#339274",
  primarySoft: "#EDF7F2",
  primaryBorder: "#D1E7DD",
  neutral: { 100: "#F3F4F6", 200: "#E5E7EB", 500: "#6B7280", 700: "#374151" },
};

type TabId = "models" | "manufacturers" | "import";

const TABS: { id: TabId; label: string; icon: React.ReactNode; description: string }[] = [
  {
    id: "models",
    label: "Equipment Models",
    icon: <Package className="w-4 h-4" />,
    description: "Manage equipment models by manufacturer",
  },
  {
    id: "manufacturers",
    label: "Manufacturers",
    icon: <Cpu className="w-4 h-4" />,
    description: "Manage manufacturer master data",
  },
  {
    id: "import",
    label: "Import from Database",
    icon: <Download className="w-4 h-4" />,
    description: "Import manufacturers & models for all solar asset types",
  },
];

export default function ManufacturerMaster() {
  const [activeTab, setActiveTab] = useState<TabId>("models");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const activeTabInfo = TABS.find((t) => t.id === activeTab)!;

  return (
    <div className="max-w-9xl mx-auto space-y-6 relative">
      <PageBreadcrumb pageTitle="Model Master" />

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Tab Header */}
        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex items-start gap-4 flex-col sm:flex-row">
            <div className="flex gap-1">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-all duration-200 whitespace-nowrap"
                  style={
                    activeTab === tab.id
                      ? {
                          color: BRAND_COLORS.primary,
                          borderBottomColor: BRAND_COLORS.primary,
                          background: BRAND_COLORS.primarySoft,
                        }
                      : {
                          color: BRAND_COLORS.neutral[500],
                          borderBottomColor: "transparent",
                          background: "transparent",
                        }
                  }
                >
                  <span
                    style={
                      activeTab === tab.id
                        ? { color: BRAND_COLORS.primary }
                        : { color: BRAND_COLORS.neutral[500] }
                    }
                  >
                    {tab.icon}
                  </span>
                  {tab.label}
                  {tab.id === "import" && (
                    <span
                      className="ml-1 px-1.5 py-0.5 text-xs rounded-full font-semibold"
                      style={{
                        background: activeTab === tab.id ? BRAND_COLORS.primary : BRAND_COLORS.neutral[100],
                        color: activeTab === tab.id ? "#fff" : BRAND_COLORS.neutral[500],
                      }}
                    >
                      NEW
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="pb-2 hidden sm:block">
              <p className="text-sm text-gray-500 mt-1">{activeTabInfo.description}</p>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "models" && (
            <MakeAndModelTable
              refreshTrigger={refreshTrigger}
              onRefresh={handleRefresh}
            />
          )}
          {activeTab === "manufacturers" && (
            <ManufacturerTable
              refreshTrigger={refreshTrigger}
              onRefresh={handleRefresh}
            />
          )}
          {activeTab === "import" && <ImportManufacturersPage />}
        </div>
      </div>
    </div>
  );
}
